import { useState } from 'react';
import PasswordField from '../PasswordField.jsx';
import FieldTip from '../FieldTip.jsx';
import { api } from '../../lib/api.js';

const SAVED_PASSWORD_MASK = '••••••••';
const MFA_METHOD_TIP =
  'To switch between an authenticator app and text messages, turn MFA off with a current code. Then turn it on again and choose the other method. Texts use the recovery phone, including the country code.';

function groupSecret(secret) {
  return String(secret || '')
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

async function authenticatorQr(otpauthUrl) {
  const mod = await import('qrcode');
  const QRCode = mod.default || mod;
  return QRCode.toDataURL(otpauthUrl, {
    margin: 1,
    width: 196,
    errorCorrectionLevel: 'M',
  });
}

export default function SecurityPanel({
  security,
  onChange,
  onSave,
  onMfaUpdated,
  onChangePassword,
  pending,
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [wantSetup, setWantSetup] = useState(false);
  const [method, setMethod] = useState('AUTHENTICATOR');
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [disableOpen, setDisableOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState('');

  const enrolled = Boolean(security.mfaEnabled) && security.mfaMethod !== 'NONE';
  const showSetupAction = !enrolled && wantSetup;
  const showDisableAction = enrolled && disableOpen;

  async function submitPassword(e) {
    e.preventDefault();
    setPasswordError('');
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    setUpdating(true);
    const result = await onChangePassword({ newPassword }).finally(() => setUpdating(false));
    if (result?.ok === false) {
      setPasswordError(result.message || 'Unable to update password.');
      return;
    }
    setNewPassword('');
    setConfirmPassword('');
  }

  function resetSetup() {
    setWantSetup(false);
    setSetup(null);
    setCode('');
    setDisableOpen(false);
  }

  async function startSetup() {
    setMfaError('');
    setMfaMessage('');
    setBusy(true);
    try {
      const data = await api.post('/server/auth_handler/mfa/setup', {
        method,
        recoveryPhone: security.recoveryPhone || '',
      });
      const next = {
        method: data.method || method,
        secret: data.secret || '',
        otpauthUrl: data.otpauthUrl || '',
        hint: data.hint || '',
        qr: '',
      };
      if (next.otpauthUrl) next.qr = await authenticatorQr(next.otpauthUrl);
      setSetup(next);
      setCode('');
    } catch (err) {
      setMfaError(err.message || 'Unable to start MFA setup.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmSetup() {
    setMfaError('');
    setBusy(true);
    try {
      const data = await api.post('/server/auth_handler/mfa/confirm', { code });
      onMfaUpdated({
        mfaEnabled: true,
        mfaMethod: data.method || method,
        recoveryPhone: data.recoveryPhone || security.recoveryPhone || '',
      });
      resetSetup();
      setMfaMessage('MFA is on. The next sign-in will ask for a code.');
    } catch (err) {
      setMfaError(err.message || 'That code is incorrect.');
    } finally {
      setBusy(false);
    }
  }

  async function openDisable() {
    setMfaError('');
    setMfaMessage('');
    setCode('');
    if (security.mfaMethod !== 'SMS') {
      setDisableOpen(true);
      return;
    }
    setBusy(true);
    try {
      const data = await api.post('/server/auth_handler/mfa/disable-code', {});
      setDisableOpen(true);
      setMfaMessage(
        data.hint
          ? `Enter the code sent to ${data.hint} to turn MFA off.`
          : 'Enter the code from your phone to turn MFA off.'
      );
    } catch (err) {
      setMfaError(err.message || 'Unable to send a code.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisable() {
    setMfaError('');
    setBusy(true);
    try {
      await api.post('/server/auth_handler/mfa/disable', { code });
      onMfaUpdated({ mfaEnabled: false, mfaMethod: 'NONE' });
      resetSetup();
      setMfaMessage('MFA is off. Sign-in uses your password only.');
    } catch (err) {
      setMfaError(err.message || 'That code is incorrect.');
    } finally {
      setBusy(false);
    }
  }

  const enrolledLabel =
    security.mfaMethod === 'SMS'
      ? 'A text-message code is required at sign-in.'
      : 'A code from your authenticator app is required at sign-in.';

  function editSecurity(patch) {
    setSaveNote('');
    onChange(patch);
  }

  async function submitSecurity(e) {
    e.preventDefault();
    setSaveNote('');
    setSaving(true);
    try {
      const message = await onSave();
      setSaveNote(
        typeof message === 'string' && message.trim() ? message : 'Security settings saved.'
      );
    } catch (err) {
      setMfaError(err?.message || 'Unable to save security settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-form security-settings">
      <h2>Security</h2>
      <p className="settings-lead">
        Protect your account with password controls, recovery options, and MFA.
      </p>

      <form className="settings-subsection" onSubmit={submitPassword}>
        <h3>Change password</h3>
        <p className="settings-sublead">A password is saved. Enter a new password to change it.</p>
        {passwordError ? <p className="form-error">{passwordError}</p> : null}
        <div className="settings-grid password-fields">
          <div className="field password-current">
            <span>Current password</span>
            <div className="password-field no-toggle saved-password-field">
              <input
                type="password"
                value={SAVED_PASSWORD_MASK}
                readOnly
                tabIndex={-1}
                aria-readonly="true"
                autoComplete="off"
                className="settings-field-filled"
                onFocus={(e) => e.target.blur()}
              />
            </div>
          </div>
          {security.lastPasswordChangeAt ? (
            <p className="password-changed-note">
              <span className="password-changed-spacer" aria-hidden="true" />
              <span className="password-changed-when">
                Last changed {new Date(security.lastPasswordChangeAt).toLocaleString()}
              </span>
            </p>
          ) : null}
          <PasswordField
            className="password-new"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <PasswordField
            className="password-confirm"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="drawer-actions">
          <button type="submit" className="btn-primary" disabled={pending || updating}>
            {pending || updating ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>

      <form className="settings-subsection" onSubmit={submitSecurity}>
        <h3>Account recovery</h3>
        <p className="settings-sublead">
          Select a field to change it. Used if you lose access to your primary email or MFA device.
        </p>
        <div className="settings-grid">
          <label className="field">
            <span>Recovery email</span>
            <input
              type="email"
              className={String(security.recoveryEmail || '').trim() ? 'settings-field-filled' : undefined}
              value={security.recoveryEmail}
              onChange={(e) => editSecurity({ recoveryEmail: e.target.value })}
              placeholder="recovery@example.com"
            />
          </label>
          <label className="field">
            <span>Recovery phone</span>
            <input
              type="tel"
              className={String(security.recoveryPhone || '').trim() ? 'settings-field-filled' : undefined}
              value={security.recoveryPhone}
              onChange={(e) => editSecurity({ recoveryPhone: e.target.value })}
              placeholder="+1 555 0100"
            />
          </label>
        </div>

        <h3 className="settings-inline-title">
          <span className="field-label-with-tip">
            Multi-factor authentication (MFA)
            <FieldTip text={MFA_METHOD_TIP} label="Change MFA method" />
          </span>
        </h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={enrolled || wantSetup}
            onChange={(e) => {
              setSaveNote('');
              setMfaError('');
              setMfaMessage('');
              if (enrolled) {
                if (!e.target.checked) openDisable();
                else setDisableOpen(false);
                return;
              }
              setWantSetup(e.target.checked);
              if (!e.target.checked) {
                setSetup(null);
                setCode('');
              }
            }}
          />
          <span>
            <strong>Require MFA at sign-in</strong>
            <small>
              {enrolled
                ? enrolledLabel
                : 'Use an authenticator app or SMS code for every new session.'}
            </small>
          </span>
        </label>

        {mfaError ? <p className="form-error">{mfaError}</p> : null}
        {mfaMessage ? <p className="form-info">{mfaMessage}</p> : null}

        {enrolled && disableOpen ? (
          <div className="mfa-setup">
            <label className="field">
              <span>Current code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                className={code ? 'settings-field-filled' : undefined}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
              />
            </label>
          </div>
        ) : null}

        {!enrolled && wantSetup ? (
          <div className="mfa-setup">
            <fieldset className="radio-group">
              <legend>MFA method</legend>
              <label className="radio-row">
                <input
                  type="radio"
                  name="mfaMethod"
                  checked={method === 'AUTHENTICATOR'}
                  onChange={() => {
                    setMethod('AUTHENTICATOR');
                    setSetup(null);
                    setCode('');
                  }}
                />
                <span>
                  <strong>Authenticator app</strong>
                  <small>TOTP codes from Google Authenticator, Authy, or similar.</small>
                </span>
              </label>
              <label className="radio-row">
                <input
                  type="radio"
                  name="mfaMethod"
                  checked={method === 'SMS'}
                  onChange={() => {
                    setMethod('SMS');
                    setSetup(null);
                    setCode('');
                  }}
                />
                <span>
                  <strong>SMS</strong>
                  <small>One-time codes sent to your recovery phone.</small>
                </span>
              </label>
            </fieldset>

            {setup?.method === 'AUTHENTICATOR' ? (
              <div className="mfa-enroll">
                <p className="settings-sublead">
                  Scan this QR code, or enter the key manually. MFA stays off until the code matches.
                </p>
                {setup.qr ? <img className="mfa-qr" src={setup.qr} alt="Authenticator QR code" /> : null}
                <p className="mfa-secret">{groupSecret(setup.secret)}</p>
              </div>
            ) : null}

            {setup?.method === 'SMS' ? (
              <p className="settings-sublead">
                Enter the code sent to {setup.hint || 'your recovery phone'}. MFA stays off until it matches.
              </p>
            ) : null}

            {setup ? (
              <label className="field">
                <span>Authentication code</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={code ? 'settings-field-filled' : undefined}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                />
              </label>
            ) : (
              <p className="settings-sublead">
                Enter a matching code to turn MFA on. Saving recovery details does not turn it on by itself.
              </p>
            )}

          </div>
        ) : null}

        {saveNote ? (
          <p className="form-info security-save-note" role="status">
            {saveNote}
          </p>
        ) : null}

        <div className="security-actions">
          <button
            type="submit"
            className={showSetupAction || showDisableAction ? 'btn-ghost' : 'btn-primary'}
            disabled={pending || saving}
          >
            {pending || saving ? 'Saving…' : 'Save security settings'}
          </button>
          {showDisableAction ? (
            <>
              <button type="button" className="btn-ghost" onClick={() => setDisableOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy || code.length !== 6}
                onClick={confirmDisable}
              >
                {busy ? 'Turning off…' : 'Turn off MFA'}
              </button>
            </>
          ) : null}
          {showSetupAction ? (
            <button
              type="button"
              className="btn-primary"
              disabled={busy || (Boolean(setup) && code.length !== 6)}
              onClick={setup ? confirmSetup : startSetup}
            >
              {setup
                ? busy
                  ? 'Checking…'
                  : 'Turn on MFA'
                : busy
                  ? 'Starting…'
                  : method === 'SMS'
                    ? 'Text me a code'
                    : 'Set up authenticator'}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
